import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

/**
 * ExtLogin — Extension Authentication Bridge
 *
 * Flow:
 * 1. User clicks "Connect Extension" in the dashboard
 * 2. A new tab opens at /ext-login
 * 3. This page reads the active Supabase session
 * 4. Sends the access token to the extension via chrome.runtime.sendMessage
 * 5. Tab auto-closes on success
 *
 * If user is not logged in, they are redirected to /login?next=ext-login
 *
 * The extension's ID from manifest.json must match REACT_APP_EXTENSION_ID.
 * In dev mode (unpublished extension), Chrome assigns a temporary ID —
 * check chrome://extensions to find it when testing.
 */
export default function ExtLogin() {
  const [status, setStatus] = useState('connecting'); // connecting | success | error | not-installed | no-session

  useEffect(() => {
    connectExtension();
  }, []);

  async function connectExtension() {
    // 1. Get current session
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error || !session) {
      setStatus('no-session');
      // Redirect to login, then back here
      setTimeout(() => {
        window.location.href = `/login?next=ext-login`;
      }, 1500);
      return;
    }

    const extensionId = process.env.REACT_APP_EXTENSION_ID;

    if (!extensionId || extensionId === 'YOUR_EXTENSION_ID') {
      setStatus('error');
      console.error('[ExtLogin] REACT_APP_EXTENSION_ID is not set in .env');
      return;
    }

    // 2. Send token to extension
    try {
      if (!window.chrome?.runtime?.sendMessage) {
        setStatus('not-installed');
        return;
      }

      window.chrome.runtime.sendMessage(
        extensionId,
        {
          type: 'SET_AUTH_TOKEN',
          token: session.access_token,
          refreshToken: session.refresh_token,
          userId: session.user.id,
          email: session.user.email,
          expiresAt: session.expires_at,
        },
        (response) => {
          if (window.chrome.runtime.lastError) {
            console.error('[ExtLogin] Extension message error:', window.chrome.runtime.lastError.message);
            setStatus('not-installed');
            return;
          }

          if (response?.ok) {
            setStatus('success');
            setTimeout(() => window.close(), 1200);
          } else {
            setStatus('error');
          }
        }
      );
    } catch (err) {
      console.error('[ExtLogin] Unexpected error:', err);
      setStatus('error');
    }
  }

  const MESSAGES = {
    connecting:    { icon: '🔄', title: 'Connecting…',               sub: 'Sending your session to the Fenscope extension.' },
    success:       { icon: '✅', title: 'Connected!',                 sub: 'Your extension is now linked. This tab will close automatically.' },
    no_session:    { icon: '🔐', title: 'Not logged in',              sub: 'Redirecting you to login…' },
    not_installed: { icon: '🧩', title: 'Extension not found',        sub: 'Make sure the Fenscope Chrome extension is installed and active.' },
    error:         { icon: '❌', title: 'Connection failed',          sub: 'Could not connect to the extension. Please try again.' },
  };

  const msg = MESSAGES[status] || MESSAGES.connecting;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg)',
      fontFamily: 'var(--font)',
      flexDirection: 'column',
      gap: '16px',
      padding: '24px',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: '3rem' }}>{msg.icon}</div>
      <h1 style={{ fontSize: '1.4rem', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-primary)', margin: 0 }}>
        {msg.title}
      </h1>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', maxWidth: 360, margin: 0 }}>
        {msg.sub}
      </p>
      {status === 'not-installed' && (
        <a
          href="https://chrome.google.com/webstore"
          target="_blank"
          rel="noreferrer"
          className="btn btn-primary"
          style={{ marginTop: 8 }}
        >
          Get Fenscope Extension
        </a>
      )}
      {status === 'error' && (
        <button className="btn btn-secondary" onClick={connectExtension} style={{ marginTop: 8 }}>
          Try Again
        </button>
      )}
    </div>
  );
}
